import React, { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import api from "@/lib/api";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Loader2, Landmark, TrendingUp, AlertTriangle, Users } from "lucide-react";

const chartStyle = { background: "#0A1128", border: "1px solid rgba(255,255,255,0.1)", fontFamily: "JetBrains Mono", fontSize: 12 };
const fmt = (n) => `₺${(n / 1e6).toFixed(1)}M`;
const TICK = { fontSize: 11, fontFamily: "JetBrains Mono" };
const LEGEND_STYLE = { fontSize: 11 };

export default function Ledger() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/ledger").then((r) => setData(r.data)).catch(() => setData(false)); }, []);

  if (!data) return <div className="min-h-screen grid place-items-center bg-[#050A15]"><Loader2 className="h-7 w-7 animate-spin text-[#00FFFF]" /></div>;
  const { entries, summary } = data;
  const chartData = [...entries].sort((a, b) => a.year - b.year).map((e) => ({
    year: e.year, Collected: e.rent_collected, Community: e.community_allocated, Misappropriated: e.misappropriated,
  }));

  return (
    <div className="min-h-screen bg-[#050A15]">
      <Header />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 py-10">
        <p className="text-xs uppercase tracking-widest text-[#00FFFF]">Civic Transparency</p>
        <h1 className="font-display font-bold text-3xl mt-1 flex items-center gap-2"><Landmark className="h-7 w-7 text-[#00FFFF]" /> Public-Land Rent Ledger</h1>
        <p className="text-slate-400 mt-2 max-w-2xl">Tracking rents from public lands historically misappropriated via flawed policies — surfaced transparently so citizens are participants and beneficiaries.</p>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
          <Stat icon={TrendingUp} c="#00FFFF" label="Total Collected" value={fmt(summary.total_collected)} />
          <Stat icon={Users} c="#00FF7F" label="To Community" value={fmt(summary.total_allocated_to_community)} />
          <Stat icon={AlertTriangle} c="#EF4444" label="Misappropriated" value={fmt(summary.total_misappropriated)} />
          <Stat icon={TrendingUp} c="#F59E0B" label="Recovery Rate" value={`${summary.recovery_rate_pct}%`} />
        </div>

        <div className="qs-panel p-6 mt-8">
          <h3 className="font-display font-semibold mb-4">Rent Flows by Year</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData}>
              <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
              <XAxis dataKey="year" stroke="#475569" tick={TICK} />
              <YAxis stroke="#475569" tick={TICK} tickFormatter={fmt} />
              <Tooltip contentStyle={chartStyle} formatter={(v) => fmt(v)} />
              <Legend wrapperStyle={LEGEND_STYLE} />
              <Bar dataKey="Community" stackId="a" fill="#00FF7F" />
              <Bar dataKey="Misappropriated" stackId="a" fill="#EF4444" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="qs-panel mt-6 overflow-x-auto" data-testid="ledger-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-widest text-slate-500 border-b border-white/10">
                <th className="px-4 py-3">Year</th><th className="px-4 py-3">Region</th><th className="px-4 py-3">Parcel</th>
                <th className="px-4 py-3">Collected</th><th className="px-4 py-3">Community</th><th className="px-4 py-3">Misappropriated</th>
              </tr>
            </thead>
            <tbody className="font-mono">
              {entries.map((e) => (
                <tr key={e.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                  <td className="px-4 py-3 text-[#00FFFF]">{e.year}</td>
                  <td className="px-4 py-3 text-slate-200 font-sans">{e.region}<div className="text-xs text-slate-500 font-sans">{e.note}</div></td>
                  <td className="px-4 py-3 text-slate-500">{e.parcel}</td>
                  <td className="px-4 py-3 text-slate-300">{fmt(e.rent_collected)}</td>
                  <td className="px-4 py-3 text-[#00FF7F]">{fmt(e.community_allocated)}</td>
                  <td className="px-4 py-3 text-[#EF4444]">{fmt(e.misappropriated)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <Footer />
    </div>
  );
}

const Stat = ({ icon: Icon, label, value, c }) => (
  <div className="qs-panel p-5">
    <Icon className="h-5 w-5" style={{ color: c }} />
    <div className="font-mono text-xl font-bold mt-3" style={{ color: c }}>{value}</div>
    <div className="text-xs uppercase tracking-widest text-slate-500 mt-1">{label}</div>
  </div>
);
